import React from "react";
import { SAFE_TOP } from "../safeArea";
import { useDismissible } from "../../native/dismissibleStack";
import "../styles/CollectorMap.css";

/**
 * MapDiscoveryHeader — the map's top row, and the roaming bar that reports it.
 *
 * `Map` and `Listings` are a two-up segmented control rather than a scrolling
 * chip row. `Cards` left this row entirely: it was never a map mode, it is a
 * route change, and it is now the grid button TopAccountBar already renders in
 * the top-right (it used to hide itself on /map precisely because this row
 * duplicated it).
 *
 * City search collapses to a single 44px button until you use it, then takes
 * the whole row. It never shares space with the segmented control at a width
 * where both would be cramped.
 */
const SEG_ICON_MIN_PX = 340;

const MapIconSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
    />
  </svg>
);

const ListingsIconSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z" />
  </svg>
);

const SearchIconSvg = ({ size = 18, stroke = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={2.1} aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path strokeLinecap="round" d="M20 20l-3.5-3.5" />
  </svg>
);

const SEGMENTS = [
  { id: "map", mode: "map", label: "Map", Icon: MapIconSvg },
  { id: "marketplace", mode: "marketplace", label: "Listings", Icon: ListingsIconSvg },
];

const shellStyle = {
  display: "flex",
  padding: 3,
  borderRadius: 14,
  background: "rgba(255,255,255,0.96)",
  border: "1px solid rgba(15,23,42,0.08)",
  boxShadow: "0 2px 10px rgba(15,23,42,0.10)",
};

const utilityButtonStyle = {
  width: 44,
  height: 44,
  flexShrink: 0,
  borderRadius: 13,
  background: "rgba(255,255,255,0.96)",
  border: "1px solid rgba(15,23,42,0.08)",
  boxShadow: "0 2px 10px rgba(15,23,42,0.10)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#475569",
  cursor: "pointer",
  padding: 0,
};

const MapDiscoveryHeader = ({
  viewMode,
  onViewModeChange,
  rightInsetPx = 0,
  widthPx = 390,
  // ── City search (roaming) ──
  roamingEnabled = false,
  citySearchOpen = false,
  cityQuery = "",
  cityResults = [],
  onOpenCitySearch,
  onCloseCitySearch,
  onCityQueryChange,
  onSelectCity,
}) => {
  // At narrow widths the segmented control drops its icons and keeps BOTH
  // labels — a label is what tells you where you are; an icon is decoration.
  const showSegIcons = widthPx > SEG_ICON_MIN_PX;
  const searchIsOpen = roamingEnabled && citySearchOpen;

  // Android Back closes the city search the way Cancel does. Unregistered, Back
  // with the search open did nothing at all (tablet QA, versionCode 11).
  useDismissible(
    searchIsOpen,
    () => onCloseCitySearch && onCloseCitySearch(),
    "map-city-search"
  );

  return (
    <div
      data-testid="map-view-chips"
      style={{
        position: "absolute",
        top: `max(12px, ${SAFE_TOP})`,
        left: 12,
        right: 12,
        zIndex: 30,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 8,
        // Keep the row clear of the floating account/cards controls.
        paddingRight: `${Math.max(rightInsetPx - 12, 0)}px`,
        fontFamily: "Inter, sans-serif",
        pointerEvents: "none",
      }}
    >
      {searchIsOpen ? (
        // minWidth 0: as a flex item this row defaulted to min-width:auto, i.e.
        // its content's natural width (the input's intrinsic width + Cancel,
        // ~330dp). That is wider than what is left inside paddingRight at a
        // 411dp phone (~289dp), so the row overflowed the padding and Cancel
        // slid ~41dp under the grid button (tablet QA, versionCode 11 —
        // measured: box 251dp, Cancel right edge 341dp instead of ≤301dp).
        <div
          data-testid="city-search-row"
          style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "flex-start", gap: 8, pointerEvents: "auto" }}
        >
          <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                height: 44,
                padding: "0 12px",
                borderRadius: 13,
                background: "#fff",
                border: "1px solid rgba(109,40,217,0.35)",
                boxShadow: "0 4px 16px rgba(15,23,42,0.14)",
              }}
            >
              <SearchIconSvg size={17} stroke="#6d28d9" />
              {/* A plain input, so outside the theme's dir="auto" for MUI
                  fields: a city typed in Hebrew reads right to left (B-05). */}
              <input
                dir="auto"
                value={cityQuery}
                onChange={onCityQueryChange}
                placeholder="Explore another city"
                aria-label="Search cities"
                data-testid="city-search-input"
                // Opening the search is the request to type; without this the
                // keyboard stayed down and a second tap was needed.
                autoFocus
                autoComplete="off"
                style={{
                  flex: 1,
                  minWidth: 0,
                  // Fill the field's full 44px row.
                  //
                  // Without this the input is content-height (~18px) and
                  // centred, so the ~13px strips above and below it are dead to
                  // touch: the search field LOOKS 44px tall and a thumb landing
                  // near its top or bottom edge focuses nothing. Everything is
                  // border-box (index.css), and the row has no vertical
                  // padding, so 44 here is exactly the row — no overflow.
                  height: 44,
                  border: "none",
                  outline: "none",
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "#0f172a",
                  background: "transparent",
                  fontFamily: "inherit",
                }}
              />
            </div>
            {cityResults.length > 0 && (
              <div
                data-testid="city-search-results"
                className="collector-fade-enter"
                style={{
                  position: "absolute",
                  top: "calc(100% + 10px)",
                  left: 0,
                  right: 0,
                  borderRadius: 14,
                  background: "#fff",
                  border: "1px solid rgba(15,23,42,0.08)",
                  boxShadow: "0 14px 36px rgba(15,23,42,0.18)",
                  overflow: "hidden",
                }}
              >
                {cityResults.map((city, i) => (
                  <button
                    key={`${city.name}-${i}`}
                    type="button"
                    data-testid={`city-result-${i}`}
                    onClick={() => onSelectCity && onSelectCity(city)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      width: "100%",
                      minHeight: 44,
                      padding: "11px 13px",
                      border: "none",
                      borderBottom: i < cityResults.length - 1 ? "1px solid rgba(15,23,42,0.06)" : "none",
                      background: "#fff",
                      textAlign: "left",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={2} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21s7-5.6 7-11a7 7 0 10-14 0c0 5.4 7 11 7 11z" />
                      <circle cx="12" cy="10" r="2.5" />
                    </svg>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#0f172a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {city.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onCloseCitySearch}
            style={{
              height: 44,
              padding: "0 12px",
              borderRadius: 13,
              border: "1px solid rgba(15,23,42,0.10)",
              background: "rgba(255,255,255,0.96)",
              color: "#475569",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
              flexShrink: 0,
            }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, pointerEvents: "auto" }}>
            <div role="tablist" aria-label="Map page mode" style={shellStyle}>
              {SEGMENTS.map(({ id, mode, label, Icon }) => {
                const active = viewMode === mode;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    data-testid={`map-view-chip-${id}`}
                    data-active={active ? "true" : "false"}
                    onClick={() => onViewModeChange && onViewModeChange(mode)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: `0 ${showSegIcons ? 13 : 12}px`,
                      height: 44,
                      border: "none",
                      borderRadius: 11,
                      background: active ? "#1a0a2e" : "transparent",
                      color: active ? "#fff" : "#475569",
                      fontSize: 13,
                      fontWeight: 700,
                      letterSpacing: "-0.01em",
                      whiteSpace: "nowrap",
                      boxShadow: active ? "0 2px 8px rgba(26,10,46,0.28)" : "none",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {showSegIcons && <Icon />}
                    {label}
                  </button>
                );
              })}
            </div>
            {roamingEnabled && (
              <button
                type="button"
                aria-label="Search another city"
                onClick={onOpenCitySearch}
                style={utilityButtonStyle}
              >
                <SearchIconSvg />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * MapRoamingBar — says which city you are looking at, and says plainly that you
 * have not moved. Roaming publishes nothing, so the map must not imply it did.
 *
 * Lives in the same single top slot as the count pill and the failure banner:
 * only one of the three can be true at a time, which is why they share a slot
 * rather than stacking.
 */
export const MapRoamingBar = ({ cityName, subtitle, onExit, top, transition }) => (
  <div
    data-testid="roaming-banner"
    style={{
      position: "absolute",
      top,
      transition,
      left: 12,
      right: 12,
      zIndex: 12,
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 8px 8px 12px",
      borderRadius: 14,
      background: "rgba(255,255,255,0.97)",
      border: "1px solid rgba(109,40,217,0.22)",
      boxShadow: "0 6px 18px rgba(15,23,42,0.12)",
      fontFamily: "Inter, sans-serif",
    }}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6d28d9" strokeWidth={2} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
    </svg>
    <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 700,
          color: "#0f172a",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        Exploring {cityName}
      </span>
      {subtitle && (
        <span style={{ fontSize: 11, fontWeight: 500, color: "#64748b", whiteSpace: "nowrap" }}>{subtitle}</span>
      )}
    </span>
    <button
      type="button"
      data-testid="back-to-my-location"
      onClick={onExit}
      style={{
        flexShrink: 0,
        minHeight: 44,
        padding: "0 12px",
        borderRadius: 10,
        border: "none",
        background: "#6d28d9",
        color: "#fff",
        fontSize: 11.5,
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      Back
    </button>
  </div>
);

export default MapDiscoveryHeader;
